import { DOM, Utils } from '@common';
import { React, Styled } from '@common/3rd';
import {
	DeskComponentRegistry,
	FlexContainer,
	GridContainer,
	ModelContainerWidget,
	Widget
} from '@component';
import { Checker } from '@model';
import {
	CheckRule,
	DeskPanelBodyCellComponents,
	DeskPanelBodyCellProps,
	DeskPanelBodyComponents,
	DeskPanelBodyProps,
	DeskPanelComponents,
	DeskPanelFooterComponents,
	DeskPanelFooterProps,
	DeskPanelHeaderCollapsibleIconComponents,
	DeskPanelHeaderCollapsibleIconProps,
	DeskPanelHeaderComponents,
	DeskPanelHeaderTitleComponents,
	DeskPanelLabelledBodyCellComponents,
	DeskPanelLabelledBodyCellProps,
	DeskPanelLabelledBodyCellState,
	DeskPanelProps,
	DeskPanelState,
	LabellingParent,
	StyledPropsTheme,
	DeskPanelHeaderExtIconProps,
	DeskPanelHeaderExtIconComponents
} from '@my-types';
import { StyledComponents } from '@styles';
import { DeskLabellingLabel } from '../component/desk-label';

/**
 * 面板头标题
 */
class DeskPanelHeaderTitle<
	P,
	S,
	C extends DeskPanelHeaderTitleComponents
> extends ModelContainerWidget<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-panel-header-title'
			})`
				flex-grow: 1;
				display: flex;
				padding-left: 10px;
				border-width: 0;
				border-left: 4px solid;
				border-image: linear-gradient(to bottom, transparent 25%, ${props => props.theme.COLOR_PRIMARY} 25% 75%, transparent 25%);
				border-image-slice: 1;
				font-weight: bold;
    			font-size: 16px;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

/**
 * 面板头收缩展开图标
 */
class DeskPanelHeaderCollapsibleIcon<
	P extends DeskPanelHeaderCollapsibleIconProps,
	S,
	C extends DeskPanelHeaderCollapsibleIconComponents
> extends Widget<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled(StyledComponents.FAS).attrs({
				'data-widget': 'desk-panel-header-collapsible-icon'
			})`
				width: ${props => props.theme.PANEL_HEADER_HEIGHT}px;
				height: ${props => props.theme.PANEL_HEADER_HEIGHT}px;
				line-height: ${props => props.theme.PANEL_HEADER_HEIGHT}px;
				cursor: pointer;
				transition: transform 400ms;
				margin-top: 4px;
				${() => {
					if (this.isExpanded()) {
						return `
							transform: rotateX(180deg);
						`;
					}
				}}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	render() {
		const { Box } = this.getComponents();
		return <Box icon="angle-down" onClick={this.onClicked} />;
	}

	private isExpanded(): boolean {
		return this.props.expanded;
	}

	private onClicked = () => {
		this.props.onClick.call(this);
	};
}

/**
 * 面板头扩展图标
 */
class DeskPanelHeaderExtIcon<
	P extends DeskPanelHeaderExtIconProps,
	S,
	C extends DeskPanelHeaderExtIconComponents
> extends Widget<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled(StyledComponents.FAS).attrs({
				'data-widget': 'desk-panel-header-ext-icon'
			})`
				width: ${props => props.theme.PANEL_HEADER_HEIGHT}px;
				height: ${props => props.theme.PANEL_HEADER_HEIGHT}px;
				line-height: ${props => props.theme.PANEL_HEADER_HEIGHT}px;
				cursor: pointer;
				transition: transform 400ms;
				margin-top: 4px;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	render() {
		const { Box } = this.getComponents();
		return <Box icon="trash" onClick={this.onClicked} />;
	}

	private onClicked = () => {
		this.props.onClick.call(this);
	};
}

/**
 * 面板头
 */
class DeskPanelHeader<P, S, C extends DeskPanelHeaderComponents> extends FlexContainer<P, S, C> {
	/** 返回子组件的名称 */
	static getConcernedChildren() {
		return {
			title: 'panel.header.title'
		};
	}

	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-panel-header'
			})`
				display: flex;
				padding: 10px 0;
				border-bottom: 1px solid #ddd;
				// justify-content: ${this.buildAlignment()};
				// border-bottom: ${props => props.theme.BORDER};
				// border-color: ${props => props.theme.COLOR_PRIMARY};
				// border-width: ${props => props.theme.BORDER_WIDTH * 3}px;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	protected renderCollapsibleIcon(): JSX.Element | void {
		const parent = this.getParentContainer();
		if (!this.isParentCollapsible(parent)) {
			return;
		}

		return (
			<DeskPanelHeaderCollapsibleIcon
				expanded={parent.isExpanded()}
				onClick={this.onCollapsibleIconClicked}
			/>
		);
	}

	protected renderExtIcon(): JSX.Element | void {
		if (!this.getExtFunction()) {
			return;
		}
		return (
			<DeskPanelHeaderExtIcon onClick={this.onExtIconClicked}/>
		);
	}

	private getParentContainer(): any {
		return (this.props as any).parentContainer;
	}

	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.renderChildren()}
				{this.renderExtIcon()}
				{this.renderCollapsibleIcon()}
			</C.Box>
		);
	}

	private isParentCollapsible(parent: any): boolean {
		if (parent == null) {
			return false;
		}
		return !!(
			Utils.isFunction(parent.isCollapsible) &&
			Utils.isFunction(parent.collapse) &&
			Utils.isFunction(parent.expand) &&
			Utils.isFunction(parent.isExpanded) &&
			parent.isCollapsible()
		);
	}

	private onCollapsibleIconClicked = (): void => {
		const parent = this.getParentContainer();
		parent.isExpanded() ? parent.collapse() : parent.expand();
	};

	private onExtIconClicked = (): void => {
		const extFunction = this.getExtFunction();
		if (extFunction) {
			if (Utils.isFunction(extFunction)) {
				return extFunction.call(this);
			} else {
				return;
			}
		} else {
			return;
		}
	};

	private getExtFunction(): any {
		return (this.props as any).extFunction;
	}
}

/**
 * 面板主体
 */
class DeskPanelBody<
	P extends DeskPanelBodyProps,
	S,
	C extends DeskPanelBodyComponents
> extends GridContainer<P, S, C> {
	/**
	 * 可以作为子组件的layout的属性值
	 */
	static getConcernedChildren() {
		return {
			children: 'panel.body.cell.labelled',
			cells: 'panel.body.cell.labelled'
		};
	}

	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-panel-body'
			})`
				display: grid;
				grid-column-gap: ${props => props.theme.PANEL_CELL_HORIZONTAL_GAP}px;
				grid-row-gap: ${props => props.theme.PANEL_CELL_VERTICAL_GAP}px;
				align-items: start;
				padding: ${props => props.theme.PANEL_PADDING}px 0;
				grid-template-columns: ${() => this.buildGridTemplateAreas()};
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

/**
 * 面板主体单元格
 */
class DeskPanelBodyCell<
	P extends DeskPanelBodyCellProps,
	S,
	C extends DeskPanelBodyCellComponents
> extends FlexContainer<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-panel-body-cell'
			})`
				display: flex;
				position: relative;
				justify-content: ${this.buildAlignment()};
				${() => this.paintBoxPosition()}
				> a[data-widget=desk-btn],
				> div[data-widget=desk-btn-group] > a[data-widget=desk-btn] {
					margin: ${props => props.theme.BTN_VERTICAL_PADDING - props.theme.BORDER_WIDTH}px 0;
					padding-top: 0;
					padding-bottom: 0;
					line-height: 32px;
					height: 32px;
				}
				> div[data-widget=desk-btn-group] > a[data-widget=desk-btn]:not(:first-child) {
					margin-left: ${props => props.theme.BTN_GROUP_GAP}px;
				}
				/* select组件会根据Option自动设定宽度, 需要强制限制 */
				> div[data-widget=desk-select] > select {
					width: 100%;
				}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	protected paintBoxPosition(): string {
		const props = this.props;
		return `
			${props.row ? `grid-row-start: ${props.row};` : ''}
			${props.rowSpan ? `grid-row-end: span ${props.rowSpan};` : ''}
			${props.column ? `grid-column-start: ${props.column};` : ''}
			${props.columnSpan ? `grid-column-end: span ${props.columnSpan};` : ''}
		`;
	}

	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

/**
 * 包含了一个标签的面板主体单元格
 */
class DeskPanelLabelledBodyCell<
	P extends DeskPanelLabelledBodyCellProps,
	S extends DeskPanelLabelledBodyCellState,
	C extends DeskPanelLabelledBodyCellComponents
> extends DeskPanelBodyCell<P, S, C> implements LabellingParent {
	/** 标签 */
	labelRef: React.RefObject<any>;
	/** 被标签的组件 */
	labelledRef: React.RefObject<any>;

	/**
	 * 可以作为子组件的layout的属性值
	 */
	static getConcernedChildren() {
		return {
			input: null // 必须由定义指定
		};
	}

	constructor(props: any, context?: any) {
		super(props, context);

		this.labelRef = React.createRef();
		this.labelledRef = React.createRef();
	}

	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-panel-labelled-body-cell'
			})`
				display: grid;
				position: relative;
				grid-column-gap: ${props => props.theme.CELL_INNER_HORIZONTAL_GAP}px;
				grid-row-gap: ${props => props.theme.CELL_INNER_VERTICAL_GAP}px;
				grid-template-columns: ${props => this.buildGridTemplateAreas(props.theme)};

				${() => this.paintBoxPosition()}
				&:before, &:after {
					content: '';
					display: block;
					position: absolute;
					bottom: ${props => props.theme.CELL_BOTTOM_BORDER_GAP}px;
					width: 100%;
					height: ${props => props.theme.BORDER_WIDTH}px;
					// border-top: ${props => props.theme.BORDER};
					z-index: -1;
				}
				&:after {
					border-width: ${props => props.theme.BORDER_WIDTH * 2}px;
					border-color: ${props => props.theme.COLOR_PRIMARY};
					width: 0;
					${() => this.paintFocusColor()}
				}
				/* 标签样式, 但必须不是最后一个节点 */
				> span[data-widget=desk-labelling-label] {
					display: block;
					/* 只有relative, before宽度才会生效 */
					position: relative;
					/* 高度可能会变 */
					height: 100%;
					text-align: right;
					padding-right: 10px;
					&:before {
						content: '';
						display: block;
						position: absolute;
						bottom: ${props => props.theme.CELL_BOTTOM_BORDER_GAP}px;
						width: 100%;
						height: ${props => props.theme.BORDER_WIDTH}px;
						// border-top: ${props => props.theme.BORDER};
						// border-color: ${props => props.theme.COLOR_PRIMARY};
					}
				}
				/* select组件会根据Option自动设定宽度, 需要强制限制 */
				/* input[file]需要强制限制 */
				> div[data-widget=desk-select] > select,
				> div[data-widget=desk-filepicker] > input[type=file] {
					width: 100%;
				}
				${props => this.paintEditableRelated(props.theme)};
				${props => this.paintCheckFailed(props.theme)};
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	protected renderLabel(): JSX.Element | void {
		const label = this.getLabel();
		if (label) {
			return (
				<DeskLabellingLabel
					ref={this.labelRef}
					labellingParent={this as LabellingParent}
					label={label}
					required={this.isDataRequired()}
				/>
			);
		}
	}

	protected renderLabelled(): Array<any> | null | undefined {
		return React.Children.map(React.Children.only(this.props.children), (child: any) => {
			return this.renderChild(child, {
				// 设置句柄, 以便访问子组件
				ref: this.labelledRef,
				// 设置自己为父组件
				labellingParent: this
			});
		});
	}

	render() {
		const C = this.getComponents();

		return (
			<C.Box>
				{this.renderLabel()}
				{this.renderLabelled()}
			</C.Box>
		);
	}

	protected getLabel(): string | undefined {
		return this.props.label;
	}

	protected getLabelwidth(): number | undefined {
		return this.props.labelWidth;
	}

	protected buildGridTemplateAreas(theme: StyledPropsTheme): string {
		const label = this.getLabel();
		if (label) {
			// 有标签
			let labelWidth = this.getLabelwidth();
			return `${labelWidth ? labelWidth : theme.DESK_CELL_INNER_LABEL_WIDTH}px 1fr`;
		} else {
			// 没有标签, 组件占满整个格子
			return '1fr';
		}
	}

	protected paintCheckFailed(theme: StyledPropsTheme): string {
		if (!this.state.checkFailed) {
			return '';
		}
		return `
			&:before, &:after {
				opacity: 0.5;
				border-color: ${theme.COLOR_ERROR};
			}
			> span[data-widget=desk-labelling-label] {
				color: ${theme.COLOR_ERROR};
				:before {
					display: none;
				}
			}
		`;
	}

	protected paintEditableRelated(theme: StyledPropsTheme): string {
		if (this.isEditable()) {
			return '';
		}

		return `
			&:before, &:after, > span[data-widget=desk-labelling-label]:before {
				border-color: ${theme.COLOR_DISABLED};
			}
			> span[data-widget=desk-labelling-label] {
				color: ${theme.FONT_COLOR};
			}
		`;
	}

	protected paintFocusColor(): string {
		if (this.state.focus) {
			return `
				transition: width ease-in-out 300ms;
				width: 100%;
			`;
		} else {
			return '';
		}
	}

	isDataRequired(): boolean {
		const child = React.Children.toArray(this.props.children)[0] as any;
		// 如果组件是只读或者失效, 则不需要校验
		// if (!this.isEditable()) {
		// 	return false;
		// }
		const check = child.props.check;
		if (check) {
			if (Utils.isBoolean(check)) {
				// 没有定义规则
				return false;
			} else {
				// 如果有required, 则认为是有必填条件
				return Utils.toArray(check).some(
					(rule: CheckRule) => rule.do === Checker.PREDEFINED.REQUIRED
				);
			}
		} else {
			// 没有定义校验规则
			return false;
		}
	}

	isEditable(): boolean {
		const child = React.Children.toArray(this.props.children)[0] as any;
		return !(
			child.props.readonly ||
			child.props.disabled ||
			DeskComponentRegistry.isReadOnly(child.type)
		);
	}

	focusLabelled = () => {
		// 通知组件获取光标
		this.labelledRef.current.focus();
	};
	onFocusGained = (): void => {
		// 通知标签现在已经得到光标
		this.labelRef.current.focus();
		this.setState({
			focus: true
		});
	};
	onFocusLost = (): void => {
		// 通知标签现在已经失去光标
		this.labelRef.current.blur();
		this.setState({
			focus: false
		});
	};
	onCheckCompleted = (passed: boolean): void => {
		this.setState({
			checkFailed: !passed
		});
	};
}

/**
 * 面板脚
 */
class DeskPanelFooter<
	P extends DeskPanelFooterProps,
	S,
	C extends DeskPanelFooterComponents
> extends FlexContainer<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-panel-footer'
			})`
				display: flex;
				justify-content: ${this.buildAlignment()};
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

/**
 * 面板
 */
class DeskPanel<
	P extends DeskPanelProps,
	S extends DeskPanelState,
	C extends DeskPanelComponents
> extends ModelContainerWidget<P, S, C> {
	boxRef: React.RefObject<any>;

	static getConcernedChildren() {
		return {
			header: 'panel.header',
			body: 'panel.body',
			footer: 'panel.footer'
		};
	}

	constructor(props: any, context?: any) {
		super(props, context);
		this.boxRef = React.createRef();
	}

	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-panel'
			})`
				display: flex;
				flex-direction: column;
				${() =>
					this.isExpanded()
						? ''
						: `
							> div[data-widget=desk-panel-body],
							> div[data-widget=desk-panel-footer] {
								display: none;
							}
						`}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}

	render() {
		const C = this.getComponents();
		return <C.Box ref={this.boxRef}>{this.renderChildren()}</C.Box>;
	}

	isCollapsible(): boolean {
		return !!this.props.collapsible;
	}

	isExpanded(): boolean {
		return this.state.expanded == null
			? this.props.expanded == null
				? true
				: this.props.expanded!
			: this.state.expanded!;
	}

	collapse(): this {
		DOM.slideUp(this.findBodyDOM()).slideUp(this.findFooterDOM());
		this.setState({
			expanded: false
		});
		return this;
	}

	expand(): this {
		DOM.slideDown(this.findBodyDOM()).slideDown(this.findFooterDOM(), 'flex');
		this.setState({
			expanded: true
		});
		return this;
	}

	clearExpanding(): this {
		this.setState({ expanded: undefined });
		return this;
	}

	private findBodyDOM(): HTMLElement | null {
		if (!this.boxRef.current) {
			return null;
		}

		return Array.from(this.boxRef.current.children).find(
			(child: any) => child.getAttribute('data-widget') === 'desk-panel-body'
		) as HTMLElement;
	}

	private findFooterDOM(): HTMLElement | null {
		if (!this.boxRef.current) {
			return null;
		}

		return Array.from(this.boxRef.current.children).find(
			(child: any) => child.getAttribute('data-widget') === 'desk-panel-footer'
		) as HTMLElement;
	}
}

DeskComponentRegistry.register({
	panel: DeskPanel,
	'panel.header': DeskPanelHeader,
	'panel.body': DeskPanelBody,
	'panel.footer': DeskPanelFooter,
	'panel.header.title': DeskPanelHeaderTitle,
	'panel.body.cell': DeskPanelBodyCell,
	'panel.body.cell.labelled': DeskPanelLabelledBodyCell
});

export {
	DeskPanel,
	DeskPanelHeader,
	DeskPanelHeaderTitle,
	DeskPanelBody,
	DeskPanelBodyCell,
	DeskPanelLabelledBodyCell,
	DeskPanelFooter
};
